import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { QuestionnaireResponseSavePayload } from '@/types/scheduling-api';

/**
 * POST /api/QuestionnaireResponses
 *
 * Mirrors Flutter `SchedulingDataSourceImpl.saveFormResponse`
 * (`scheduling_data_source_impl.dart:415-433`). Commits the patient's
 * answers to a questionnaire attached to an appointment.
 *
 * Wire shape verified against the 2026-05-20 dev curl. The request
 * body is `QuestionnaireResponseSavePayload` — **NOT** the same shape
 * as `QuestionnaireSubmitted` (the GET response). Two critical
 * differences:
 *   - `subject` and `author` ship as PLAIN GUID strings (not the
 *     `{ _comment, patientid }` / `{ _comment, practitionerId }`
 *     objects the GET response uses)
 *   - top-level `items` is plural (vs `item` singular on GET)
 *   - every item ships all 3 `answer.value*` fields even when unused
 *     (Flutter parity)
 *
 * Backend behaviour (verified by reading
 * `Scrips.PracticeManagement/.../QuestionnaireResponseService.cs`
 * 2026-05-20):
 *   - 200 with body `true` on success — backend flips status to
 *     `"Completed"` server-side.
 *   - 400 with `QuestionnaireNotExist` if no existing `InProgress`
 *     `QuestionnaireResponses` row exists for the `(QuestionnaireId,
 *     SubjectId)` tuple. Something else creates the InProgress row
 *     first (appointment booking flow when a form is attached); this
 *     hook only UPDATES an existing row, doesn't create one. The
 *     global QueryClient `onError` handler surfaces the 400 as a
 *     toast.
 *   - Backend does NOT validate completeness — Plan A (PROD-845)
 *     enforces required-field rules client-side only.
 *
 * On 200 we invalidate the PR-1 GET (`questionnaire-response`) so the
 * Forms tab observer refetches and transitions from the editable form
 * → the read-only submitted view automatically.
 */

interface SaveQuestionnaireResponseVariables {
  /** The appointment whose questionnaire is being answered. Used for
   *  the cache invalidation key — must match the `appointmentId`
   *  passed to `useQuestionnaireResponseForPatient`. */
  appointmentId: string;
  payload: QuestionnaireResponseSavePayload;
}

export function useSaveQuestionnaireResponse(): UseMutationResult<
  boolean,
  Error,
  SaveQuestionnaireResponseVariables
> {
  const qc = useQueryClient();
  return useMutation<boolean, Error, SaveQuestionnaireResponseVariables>({
    mutationFn: ({ payload }) =>
      post<boolean>('/api/QuestionnaireResponses', payload),
    onSuccess: (_data, vars) => {
      // Invalidate the GET that powers the Forms tab read view — the
      // tab observer will refetch and transition to the submitted view.
      void qc.invalidateQueries({
        queryKey: ['questionnaire-response', vars.appointmentId],
      });
    },
  });
}
